import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { analyticsService } from '@/services/analyticsService';

interface SpoilAccessSwitchProps {
  isSpoil: boolean;
  canDisableSpoil: boolean;
  onSpoilChange: (value: boolean) => void;
  label?: string;
  theme?: 'white' | 'sepia' | 'dark';
  bookId?: number;
  bookTitle?: string;
  subscriptionButtonType?: string;
}

export default function SpoilAccessSwitch({
  isSpoil,
  canDisableSpoil,
  onSpoilChange,
  label = 'لو نده',
  theme = 'white',
  bookId,
  bookTitle,
  subscriptionButtonType = 'spoiler_access',
}: SpoilAccessSwitchProps) {
  const navigate = useNavigate();
  const [showPaywall, setShowPaywall] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({
    placeAbove: false,
    left: 0,
    width: 256,
    arrowLeft: 6,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const switchRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const spoilerProtectionEnabled = canDisableSpoil ? !isSpoil : true;
  const labelClass = theme === 'dark'
    ? 'text-gray-100'
    : theme === 'sepia'
      ? 'text-[#5c4a2f]'
      : 'text-gray-700';
  const activeClass = theme === 'dark'
    ? 'bg-[#6f63ff]'
    : theme === 'sepia'
      ? 'bg-[#8a5d28]'
      : 'bg-[#6f63ff]';

  useEffect(() => {
    if (canDisableSpoil) setShowPaywall(false);
  }, [canDisableSpoil]);

  useLayoutEffect(() => {
    if (!showPaywall || !switchRef.current || !wrapperRef.current) return;
    const updatePlacement = () => {
      const switchRect = switchRef.current?.getBoundingClientRect();
      const wrapperRect = wrapperRef.current?.getBoundingClientRect();
      if (!switchRect || !wrapperRect) return;

      const width = Math.min(256, window.innerWidth - 16);
      const switchCenter = switchRect.left + switchRect.width / 2;
      const viewportLeft = Math.max(
        8,
        Math.min(switchCenter - width / 2, window.innerWidth - width - 8)
      );
      setTooltipPosition({
        placeAbove: switchRect.bottom + 128 > window.innerHeight,
        left: viewportLeft - wrapperRect.left,
        width,
        arrowLeft: Math.max(8, Math.min(switchCenter - viewportLeft - 8, width - 24)),
      });
    };
    updatePlacement();
    window.addEventListener('resize', updatePlacement);
    window.addEventListener('scroll', updatePlacement, true);
    return () => {
      window.removeEventListener('resize', updatePlacement);
      window.removeEventListener('scroll', updatePlacement, true);
    };
  }, [showPaywall]);

  useEffect(() => {
    if (!showPaywall) return;
    const dismissOnOutsideInteraction = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (switchRef.current?.contains(target) || tooltipRef.current?.contains(target)) return;
      setShowPaywall(false);
    };
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowPaywall(false);
    };
    document.addEventListener('mousedown', dismissOnOutsideInteraction);
    document.addEventListener('touchstart', dismissOnOutsideInteraction);
    document.addEventListener('keydown', dismissOnEscape);
    return () => {
      document.removeEventListener('mousedown', dismissOnOutsideInteraction);
      document.removeEventListener('touchstart', dismissOnOutsideInteraction);
      document.removeEventListener('keydown', dismissOnEscape);
    };
  }, [showPaywall]);

  const handleClick = () => {
    if (!canDisableSpoil && spoilerProtectionEnabled) {
      setShowPaywall(previous => !previous);
      return;
    }
    onSpoilChange(!isSpoil);
  };

  return (
    <div ref={wrapperRef} className="relative inline-flex items-center gap-2" dir="rtl">
      {label && <span className={cn('font-persian text-xs font-semibold', labelClass)}>{label}</span>}
      <button
        ref={switchRef}
        type="button"
        role="switch"
        aria-checked={spoilerProtectionEnabled}
        aria-label={label}
        onPointerDown={event => event.stopPropagation()}
        onClick={event => {
          event.stopPropagation();
          handleClick();
        }}
        className={cn(
          'relative h-4 w-7 rounded-full transition-colors',
          spoilerProtectionEnabled ? activeClass : 'bg-gray-300'
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform',
            spoilerProtectionEnabled ? 'translate-x-0.5' : 'translate-x-[14px]'
          )}
        />
      </button>

      {showPaywall && (
        <div
          ref={tooltipRef}
          className={cn(
            'absolute z-[10000] max-w-[calc(100vw-16px)] overflow-y-auto rounded-xl border border-gray-200 bg-white p-3 text-right shadow-xl',
            tooltipPosition.placeAbove ? 'bottom-full mb-2' : 'top-full mt-2'
          )}
          style={{
            left: tooltipPosition.left,
            width: tooltipPosition.width,
            maxHeight: 'min(14rem, calc(100dvh - 16px))',
          }}
          role="dialog"
          aria-label="نیاز به اشتراک"
        >
          <span
            aria-hidden="true"
            className={cn(
              'absolute h-0 w-0 border-x-[8px] border-x-transparent',
              tooltipPosition.placeAbove ? '-bottom-2 border-t-[8px] border-t-white' : '-top-2 border-b-[8px] border-b-white'
            )}
            style={{ left: tooltipPosition.arrowLeft }}
          />
          <p className="font-persian text-xs leading-6 text-gray-800">
            برای غیرفعال کردن «لو نده» باید اشتراک داشته باشی!
          </p>
          <button
            type="button"
            onClick={() => {
              void analyticsService.logEvent('subscription_purchase_button_click', {
                book_id: String(bookId ?? 'unknown'),
                book_title: bookTitle || 'unknown',
                button_type: subscriptionButtonType,
              });
              navigate('/premium-subscription');
            }}
            className="mt-2 w-full rounded-lg bg-[#6f63ff] px-3 py-2 font-persian text-xs font-bold text-white"
          >
            خرید اشتراک
          </button>
        </div>
      )}
    </div>
  );
}
